import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, X, ZoomIn, ZoomOut } from "lucide-react";
import { downloadMessageAttachment } from "./messagingUi";

/**
 * Full-screen attachment preview modal.
 * Rendered into document.body via React portal to escape scroll containers
 * and stacking contexts inside the chat UI.
 *
 * Props:
 *   attachment  – result of getMessageAttachmentMeta()
 *                 { url, label, mimeType, kind, sizeLabel }
 *   onClose     – callback to dismiss the modal
 */
export default function AttachmentPreviewModal({ attachment, onClose }) {
  const [zoomed, setZoomed] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const backdropRef = useRef(null);

  /* -- Escape key + body scroll lock -- */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleBackdrop = useCallback(
    (e) => {
      if (e.target === backdropRef.current) onClose();
    },
    [onClose],
  );

  const handleDownload = useCallback(async (e) => {
    e.stopPropagation();
    if (!(attachment?.downloadUrl || attachment?.url) || downloading) return;

    setDownloading(true);
    setDownloadError("");
    try {
      await downloadMessageAttachment(attachment);
    } catch (error) {
      setDownloadError(
        error?.message || "Unable to download this attachment right now.",
      );
    } finally {
      setDownloading(false);
    }
  }, [attachment, downloading]);

  const isImage = attachment.kind === "image" && !imgError;
  const isVideo = attachment.kind === "video";

  const modal = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 sm:p-6"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${attachment.label}`}
    >
      {/* -- Top-right action bar -- */}
      <div className="absolute right-3 top-3 z-10 flex items-center gap-2 sm:right-4 sm:top-4">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20"
          aria-label={`Download ${attachment.label}`}
        >
          <Download size={16} />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20"
          aria-label="Close preview"
        >
          <X size={18} />
        </button>
      </div>

      {/* -- Image -- */}
      {attachment.kind === "image" && !imgError && (
        <div
          className="flex max-h-full max-w-full flex-col items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={attachment.url}
            alt={attachment.label}
            onError={() => setImgError(true)}
            className={`block rounded-xl shadow-2xl transition-all duration-200 ${
              zoomed
                ? "max-h-[93vh] max-w-[95vw] cursor-zoom-out object-contain"
                : "max-h-[80vh] max-w-[min(90vw,900px)] cursor-zoom-in object-contain"
            }`}
            onClick={() => setZoomed((z) => !z)}
          />
          <button
            type="button"
            onClick={() => setZoomed((z) => !z)}
            className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[11px] text-neutral-200 backdrop-blur-sm transition hover:bg-white/20"
          >
            {zoomed ? (
              <>
                <ZoomOut size={12} />
                <span>Zoom out</span>
              </>
            ) : (
              <>
                <ZoomIn size={12} />
                <span>Zoom in</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* -- Image load error fallback -- */}
      {attachment.kind === "image" && imgError && (
        <div
          className="flex flex-col items-center gap-4 rounded-2xl bg-neutral-800 p-8 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <FileText size={40} className="text-neutral-400" />
          <div>
            <p className="text-sm font-medium text-white">{attachment.label}</p>
            <p className="mt-1 text-xs text-neutral-400">Image could not be loaded.</p>
          </div>
          <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full bg-neutral-600 px-5 py-2 text-sm text-white transition hover:bg-neutral-500"
            onClick={(e) => e.stopPropagation()}
          >
            <Download size={14} />
            <span>Open file</span>
          </a>
        </div>
      )}

      {/* -- Video -- */}
      {isVideo && (
        <div
          className="flex max-h-full max-w-full flex-col items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <video
            src={attachment.url}
            controls
            playsInline
            className="max-h-[85vh] max-w-[min(90vw,900px)] rounded-xl shadow-2xl"
          />
        </div>
      )}

      {/* -- File / document -- */}
      {attachment.kind === "file" && (
        <div
          className="flex flex-col items-center gap-5 rounded-2xl bg-neutral-800 px-10 py-10 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-neutral-700">
            <FileText size={40} className="text-neutral-300" />
          </div>
          <div>
            <p className="max-w-[260px] break-words text-base font-semibold text-white">
              {attachment.label}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-xs text-neutral-400">
              {attachment.sizeLabel && <span>{attachment.sizeLabel}</span>}
              {attachment.mimeType && <span>{attachment.mimeType}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-2 rounded-full bg-neutral-600 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-500"
          >
            <Download size={15} />
            <span>{downloading ? "Preparing..." : "Download file"}</span>
          </button>
        </div>
      )}

      {downloadError ? (
        <div className="absolute bottom-14 left-1/2 z-10 max-w-[min(90vw,28rem)] -translate-x-1/2 rounded-2xl border border-rose-400/30 bg-rose-500/15 px-3 py-2 text-center text-[11px] text-rose-100 backdrop-blur-sm">
          {downloadError}
        </div>
      ) : null}

      {/* -- Bottom filename label (image & video only) -- */}
      {(isImage || isVideo) && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-[80vw] truncate rounded-full bg-black/60 px-4 py-1.5 text-center text-xs text-neutral-300 backdrop-blur-sm">
          {attachment.label}
          {attachment.sizeLabel ? ` · ${attachment.sizeLabel}` : ""}
        </div>
      )}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}
